import {
  collection,
  deleteDoc,
  doc,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  Timestamp,
  where,
  type DocumentData,
  type FirestoreDataConverter,
  type Query,
  type WithFieldValue,
} from 'firebase/firestore';

import { firestore } from './config';
import { USE_API, api, apiQuery } from '@/services/api/client';
import type { Formulario, UsoFormulario } from '@/types/domain';

const formularioConverter: FirestoreDataConverter<Formulario> = {
  toFirestore(data: WithFieldValue<Formulario>): DocumentData {
    return data as DocumentData;
  },
  fromFirestore(snap, options) {
    const data = snap.data(options) as Omit<Formulario, 'id'>;
    return { id: snap.id, ...data };
  },
};

const usoConverter: FirestoreDataConverter<UsoFormulario> = {
  toFirestore(data: WithFieldValue<UsoFormulario>): DocumentData {
    return data as DocumentData;
  },
  fromFirestore(snap, options) {
    const data = snap.data(options) as Omit<UsoFormulario, 'id'>;
    return { id: snap.id, ...data };
  },
};

export const formulariosCollection = collection(firestore, 'formularios').withConverter(formularioConverter);
export const usosFormularioCollection = collection(firestore, 'usos_formulario').withConverter(usoConverter);

export const formulariosQuery: any = USE_API
  ? apiQuery('/formularios', { _order: 'nombre' })
  : query(formulariosCollection, orderBy('nombre'));

export function usosFormularioQuery(formularioId: string): any {
  if (USE_API) return apiQuery('/usos_formulario', { formularioId, _order: '-fecha' });
  return query(usosFormularioCollection, where('formularioId', '==', formularioId), orderBy('fecha', 'desc'));
}

export interface UpsertFormularioInput {
  id?: string;
  nombre: string;
  descripcion?: string;
  stock: number;
  stockMin: number;
  ubicacion?: string;
  notas?: string;
}

export async function upsertFormulario(input: UpsertFormularioInput) {
  const stockBajo = input.stock <= input.stockMin;
  if (USE_API) {
    const body = { ...input, stockBajo };
    if (input.id) return (await api.patch(`/formularios/${input.id}`, body)).id;
    return (await api.post('/formularios', body)).id;
  }
  const id = input.id ?? doc(formulariosCollection).id;
  const ref = doc(formulariosCollection, id);
  await setDoc(
    ref,
    {
      ...input,
      stockBajo,
      updatedAt: serverTimestamp(),
      ...(input.id ? {} : { createdAt: serverTimestamp() }),
    } as any,
    { merge: true },
  );
  return id;
}

export async function eliminarFormulario(id: string) {
  if (USE_API) return void (await api.delete(`/formularios/${id}`));
  await deleteDoc(doc(formulariosCollection, id));
}

export interface UsarFormularioInput {
  formularioId: string;
  cantidad: number;
  numeroTalonario?: string;
  solicitadoPor?: string;
  motivo?: string;
  autorUid: string;
  autorNombre: string;
}

export async function usarFormulario(input: UsarFormularioInput) {
  if (USE_API) {
    await api.post('/acciones/usar-formulario', input);
    return;
  }
  await runTransaction(firestore, async (tx) => {
    const ref = doc(formulariosCollection, input.formularioId);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('El formulario ya no existe.');
    const f = snap.data();
    if ((f.stock ?? 0) < input.cantidad) {
      throw new Error(`No hay suficientes formularios. Quedan ${f.stock ?? 0}.`);
    }
    const nuevoStock = (f.stock ?? 0) - input.cantidad;
    tx.update(ref, {
      stock: nuevoStock,
      stockBajo: nuevoStock <= (f.stockMin ?? 0),
      updatedAt: serverTimestamp(),
    });
    const usoRef = doc(usosFormularioCollection, `uso-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    tx.set(usoRef, {
      formularioId: input.formularioId,
      formularioNombre: f.nombre,
      cantidad: input.cantidad,
      numeroTalonario: input.numeroTalonario ?? null,
      solicitadoPor: input.solicitadoPor ?? null,
      motivo: input.motivo ?? null,
      fecha: Timestamp.now(),
      autorUid: input.autorUid,
      autorNombre: input.autorNombre,
    } as any);
  });
}

export interface AjustarStockFormularioInput {
  formularioId: string;
  delta: number;
  motivo?: string;
  autorUid: string;
  autorNombre: string;
}

export async function ajustarStockFormulario(input: AjustarStockFormularioInput) {
  if (USE_API) {
    await api.post('/acciones/ajustar-formulario', input);
    return;
  }
  await runTransaction(firestore, async (tx) => {
    const ref = doc(formulariosCollection, input.formularioId);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('El formulario ya no existe.');
    const f = snap.data();
    const nuevoStock = Math.max(0, (f.stock ?? 0) + input.delta);
    tx.update(ref, {
      stock: nuevoStock,
      stockBajo: nuevoStock <= (f.stockMin ?? 0),
      updatedAt: serverTimestamp(),
    });
    const usoRef = doc(usosFormularioCollection, `ajuste-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    tx.set(usoRef, {
      formularioId: input.formularioId,
      formularioNombre: f.nombre,
      cantidad: input.delta,
      numeroTalonario: null,
      solicitadoPor: null,
      motivo: input.motivo ?? (input.delta > 0 ? 'Ingreso de stock' : 'Ajuste de inventario'),
      fecha: Timestamp.now(),
      autorUid: input.autorUid,
      autorNombre: input.autorNombre,
    } as any);
  });
}
